import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSiteName } from '../contexts/SiteSettingsContext';
import { useAffiliate } from '../contexts/AffiliateContext';

const API = process.env.REACT_APP_BACKEND_URL;

const GuestCheckoutPage = () => {
  const navigate = useNavigate();
  const siteName = useSiteName();
  const { getAttributionSnapshot, bindEmail } = useAffiliate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '' });
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [pincodeLookup, setPincodeLookup] = useState({ loading: false, error: '', autoFilled: false });
  const [serviceability, setServiceability] = useState(null);
  const [recs, setRecs] = useState({ basket_tiers: [{ min_qty: 2, discount_percent: 5 }, { min_qty: 3, discount_percent: 10 }] });

  useEffect(() => {
    axios.get(`${API}/api/cart/recommendations`).then((r) => setRecs(r.data || {})).catch(() => {});
  }, []);

  const lookupPincode = async (pc) => {
    if (pc.length !== 6) {
      setServiceability(null);
      setPincodeLookup({ loading: false, error: '', autoFilled: false });
      return;
    }
    setPincodeLookup({ loading: true, error: '', autoFilled: false });
    setServiceability(null);
    try {
      const [det, srv] = await Promise.all([
        axios.get(`${API}/api/shipping/postcode-details/${pc}`).then(r => r.data).catch((e) => ({ _err: e.response?.data?.detail || 'Pincode lookup failed' })),
        axios.get(`${API}/api/shipping/serviceability`, { params: { pincode: pc } }).then(r => r.data).catch(() => null),
      ]);
      if (det && !det._err && (det.city || det.state)) {
        setForm((f) => ({ ...f, city: det.city || f.city, state: det.state || f.state }));
        setPincodeLookup({ loading: false, error: '', autoFilled: true });
      } else {
        setPincodeLookup({ loading: false, error: det._err || 'Pincode not found', autoFilled: false });
      }
      setServiceability(srv);
    } catch {
      setPincodeLookup({ loading: false, error: 'Pincode lookup failed', autoFilled: false });
    }
  };

  const onPincodeChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    setForm({ ...form, pincode: v });
    if (v.length === 6) lookupPincode(v);
    else { setServiceability(null); setPincodeLookup({ loading: false, error: '', autoFilled: false }); }
  };

  useEffect(() => {
    axios.get(`${API}/api/products`).then(r => setProducts(r.data)).catch(() => {});
    const saved = localStorage.getItem('guest_cart');
    if (saved) setCart(JSON.parse(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem('guest_cart', JSON.stringify(cart));
  }, [cart]);

  const addItem = (productId) => {
    const existing = cart.find(c => c.product_id === productId);
    const product = products.find(p => p.product_id === productId);
    if (!product) return;
    if (existing) {
      if (existing.quantity >= product.stock) { toast.error(`Only ${product.stock} units available`); return; }
      setCart(cart.map(c => c.product_id === productId ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { product_id: productId, quantity: 1 }]);
    }
    toast.success('Added to cart');
  };

  const removeItem = (productId) => setCart(cart.filter(c => c.product_id !== productId));

  const totalQty = cart.reduce((s, it) => s + (it.quantity || 0), 0);

  const basketTiersPct = (() => {
    const tiers = Array.isArray(recs.basket_tiers) ? recs.basket_tiers : [];
    if (!tiers.length) return 0;
    const apl = tiers.filter((t) => totalQty >= Number(t.min_qty || 0));
    return apl.length ? Math.max(...apl.map((t) => Number(t.discount_percent || 0))) : 0;
  })();

  const grossTotal = cart.reduce((sum, item) => {
    const p = products.find((pr) => pr.product_id === item.product_id);
    return sum + (p ? p.price * item.quantity : 0);
  }, 0);

  // Mutual exclusivity: when a coupon is applied, the basket-tier discount is skipped.
  const basketDiscount = discount > 0 ? 0 : +(grossTotal * basketTiersPct / 100).toFixed(2);

  // getTotal returns the pre-coupon basket total; the coupon `discount` is
  // subtracted once at `finalTotal` (preserves the existing two-line pattern).
  const getTotal = () => Math.max(0, grossTotal - basketDiscount);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await axios.post(`${API}/api/coupons/validate`, { code: couponCode, cart_total: getTotal() });
      setDiscount(data.discount);
      toast.success(`Coupon applied! ₹${data.discount} off`);
    } catch (err) {
      setDiscount(0);
      toast.error(err.response?.data?.detail || 'Invalid coupon');
    }
  };

  const handleCheckout = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email || !form.phone) { toast.error('First name, last name, email, and phone are required'); return; }
    if (!form.line1 || !form.city || !form.state) { toast.error('Complete address is required'); return; }
    if (!form.pincode || form.pincode.length !== 6) { toast.error('Valid 6-digit pincode required'); return; }
    if (!serviceability || !serviceability.serviceable) { toast.error('Selected pincode is not serviceable. Please try a different one.'); return; }
    if (!consent) { toast.error('Please accept the consent to continue'); return; }
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    setLoading(true);
    try {
      if (form.email) bindEmail(form.email);
      const { data: orderData } = await axios.post(`${API}/api/checkout/guest/create-order`, {
        ...form, items: cart, coupon_code: couponCode || null, consent, ...getAttributionSnapshot()
      });

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        const options = {
          key: orderData.key_id,
          amount: orderData.amount,
          currency: orderData.currency,
          name: siteName,
          description: 'Guest Order',
          order_id: orderData.order_id,
          handler: async (response) => {
            try {
              await axios.post(`${API}/api/checkout/guest/verify-payment`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast.success('Order placed successfully!');
              localStorage.removeItem('guest_cart');
              navigate('/');
            } catch { toast.error('Payment verification failed'); }
          },
          prefill: { name: `${form.first_name} ${form.last_name}`.trim(), email: form.email, contact: form.phone },
          theme: { color: '#2E7D32' },
          modal: { ondismiss: () => setLoading(false) }
        };
        const rz = new window.Razorpay(options);
        rz.on('payment.failed', (r) => { toast.error('Payment failed: ' + r.error.description); setLoading(false); });
        rz.open();
        setLoading(false);
      };
      script.onerror = () => { toast.error('Failed to load payment gateway'); setLoading(false); };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  const total = getTotal();
  const finalTotal = Math.max(0, total - discount);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="container mx-auto px-4 py-12 pt-20 max-w-4xl">
        <h1 className="text-4xl font-black font-heading mb-2" data-testid="guest-checkout-heading">Guest Checkout</h1>
        <p className="text-muted-foreground mb-8 text-sm">No account needed. Just pick your teas, enter your details, and pay.</p>

        {step === 1 && (
          <div data-testid="guest-step-1">
            <h2 className="font-heading font-semibold text-lg mb-4">1. Select Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {products.filter(p => p.stock > 0).map(p => (
                <div key={p.product_id} className="p-3 rounded-xl bg-white border border-[#E0E0E0]" data-testid={`guest-product-${p.product_id}`}>
                  <img src={p.image_url} alt={p.name} className="w-full aspect-square object-cover rounded-lg mb-2" />
                  <h3 className="font-heading font-semibold text-xs">{p.name}</h3>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-primary font-bold text-sm">{'\u20B9'}{p.price}</span>
                    <Button size="sm" className="rounded-full h-7 text-[10px] px-2" onClick={() => addItem(p.product_id)} data-testid={`guest-add-${p.product_id}`}>Add</Button>
                  </div>
                </div>
              ))}
            </div>

            {cart.length > 0 && (
              <div className="p-5 rounded-xl bg-white border border-[#E0E0E0] mb-4">
                <h3 className="font-heading font-semibold mb-3">Your Cart ({cart.length} items)</h3>
                <div className="rounded-md border-2 border-red-500/60 bg-red-50 px-3 py-2 mb-3 text-xs" data-testid="guest-discount-rules-banner">
                  <p className="font-bold text-red-700 mb-0.5">Discount rules</p>
                  <ul className="text-red-700 list-disc pl-4 space-y-0.5">
                    <li>Buy <b>2+</b> products → automatic <b>5% off</b>.</li>
                    <li>Buy <b>3+</b> products → automatic <b>10% off</b>.</li>
                    <li>If you apply a valid <b>coupon</b>, product/basket discounts will <b>not</b> apply.</li>
                  </ul>
                </div>
                {cart.map(item => {
                  const p = products.find(pr => pr.product_id === item.product_id);
                  if (!p) return null;
                  return (
                    <div key={item.product_id} className="flex justify-between items-center py-2 border-b border-[#E0E0E0] last:border-0" data-testid={`guest-cart-item-${item.product_id}`}>
                      <div>
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground">Qty: {item.quantity} x {'\u20B9'}{p.price}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-primary">{'\u20B9'}{(p.price * item.quantity).toFixed(2)}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(item.product_id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-between text-sm pt-2">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span data-testid="guest-subtotal">{'\u20B9'}{grossTotal.toFixed(2)}</span>
                </div>
                {basketDiscount > 0 && (
                  <div className="flex justify-between text-sm text-primary" data-testid="guest-basket-discount">
                    <span>Bulk discount ({basketTiersPct}% off)</span>
                    <span>-{'\u20B9'}{basketDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold mt-1 pt-2 border-t">
                  <span>Total</span>
                  <span className="text-primary" data-testid="guest-total">{'\u20B9'}{total.toFixed(2)}</span>
                </div>
                <Button className="w-full mt-4 rounded-full py-3 min-h-[44px] md:min-h-0 md:py-2" onClick={() => setStep(2)} data-testid="guest-proceed-to-details">Continue to Details</Button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div data-testid="guest-step-2">
            <Button variant="ghost" onClick={() => setStep(1)} className="mb-4 text-sm">Back to Cart</Button>
            <h2 className="font-heading font-semibold text-lg mb-4">2. Your Details & Delivery Address</h2>
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} required data-testid="guest-first-name" /></div>
                <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} required data-testid="guest-last-name" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Phone *</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required data-testid="guest-phone" /></div>
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="guest-email" /></div>
              </div>
              <div><Label>Address Line 1 *</Label><Input value={form.line1} onChange={e => setForm({...form, line1: e.target.value})} required data-testid="guest-line1" /></div>
              <div><Label>Address Line 2</Label><Input value={form.line2} onChange={e => setForm({...form, line2: e.target.value})} data-testid="guest-line2" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Pincode *</Label>
                  <Input value={form.pincode} onChange={onPincodeChange} maxLength={6} required data-testid="guest-pincode" />
                </div>
                <div><Label>City *</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} required data-testid="guest-city" placeholder={pincodeLookup.loading ? 'Looking up…' : ''} /></div>
                <div><Label>State *</Label><Input value={form.state} onChange={e => setForm({...form, state: e.target.value})} required data-testid="guest-state" placeholder={pincodeLookup.loading ? 'Looking up…' : ''} /></div>
              </div>
              {pincodeLookup.autoFilled && (
                <p className="text-xs text-primary mt-1" data-testid="guest-pincode-autofilled">City &amp; state auto-filled from pincode {form.pincode}.</p>
              )}
              {pincodeLookup.error && (
                <p className="text-xs text-destructive mt-1" data-testid="guest-pincode-error">{pincodeLookup.error}. Please enter city &amp; state manually.</p>
              )}
              {serviceability && !serviceability.serviceable && (
                <div className="mt-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20" data-testid="guest-serviceability-error">
                  <p className="text-xs text-destructive font-medium">Sorry, pincode {form.pincode} is not serviceable.</p>
                  <p className="text-[11px] text-muted-foreground">{serviceability.message || 'We do not deliver here yet.'}</p>
                </div>
              )}
              {serviceability && serviceability.serviceable && (
                <p className="text-xs text-primary mt-1" data-testid="guest-serviceability-ok">
                  Deliverable in {serviceability.estimated_days} days{serviceability.min_rate ? ` · starts at ₹${Math.round(serviceability.min_rate)}` : ''}.
                </p>
              )}
            </div>

            {/* Coupon */}
            <div className="p-5 rounded-xl bg-white border border-[#E0E0E0] mb-6">
              <Label>Coupon Code</Label>
              <div className="flex gap-2 mt-1">
                <Input value={couponCode} onChange={e => setCouponCode(e.target.value)} placeholder="Enter code" data-testid="guest-coupon-input" />
                <Button variant="outline" onClick={applyCoupon} data-testid="guest-apply-coupon">Apply</Button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="p-5 rounded-xl bg-white border border-[#E0E0E0] mb-6">
              <h3 className="font-heading font-semibold mb-3">Order Summary</h3>
              {cart.map(item => {
                const p = products.find(pr => pr.product_id === item.product_id);
                return p ? <div key={item.product_id} className="flex justify-between text-sm py-1"><span>{p.name} x{item.quantity}</span><span>{'\u20B9'}{(p.price * item.quantity).toFixed(2)}</span></div> : null;
              })}
              {discount > 0 && <div className="flex justify-between text-sm py-1 text-green-600"><span>Discount</span><span>-{'\u20B9'}{discount.toFixed(2)}</span></div>}
              <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t"><span>Total</span><span className="text-primary">{'\u20B9'}{finalTotal.toFixed(2)}</span></div>
            </div>

            {/* Consent */}
            <div className="flex items-start gap-3 mb-6 p-4 rounded-xl bg-muted" data-testid="guest-consent-section">
              <Checkbox id="consent" checked={consent} onCheckedChange={setConsent} data-testid="guest-consent-checkbox" />
              <label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                I agree to receive order updates via SMS, email, and WhatsApp. <a href="/page/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">Privacy Policy</a>
              </label>
            </div>

            <Button className="w-full rounded-full" size="lg" onClick={handleCheckout} disabled={loading || !consent || (form.pincode.length === 6 && (!serviceability || !serviceability.serviceable))} data-testid="guest-pay-button">
              {loading ? 'Processing...' : (form.pincode.length === 6 && serviceability && !serviceability.serviceable) ? 'Pincode not serviceable' : `Pay ${'\u20B9'}${finalTotal.toFixed(2)} with Razorpay`}
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default GuestCheckoutPage;
